import React, { useCallback, useState } from 'react';

import { defineMessages, useIntl } from 'react-intl-next';

import { type UIAnalyticsEvent } from '@atlaskit/analytics-next';
import Button from '@atlaskit/button/new';
import { ErrorMessage } from '@atlaskit/form';
import Modal, { ModalBody, ModalFooter, ModalHeader, ModalTitle } from '@atlaskit/modal-dialog';
import { Box, Text, xcss } from '@atlaskit/primitives';

const errStyles = xcss({
	marginTop: 'space.150',
});

type Props = {
	onClose?: () => void;
	onDelete?: () => Promise<void>;
	agentId?: string;
	agentName?: string;
};

type Messages = keyof typeof messages;
const agentResponseMessageTemplate: { [key: string]: Messages } = {
	AGENT_DOES_NOT_EXIST: 'errorAgentNotFound',
	USER_NOT_CREATOR: 'errorIncorrectOwner',
	AGENT_HAS_CONVERSATIONS: 'errorAgentInUse',
	AGENT_DELETE_SUCCESS: 'agentDeletedSuccess',
};

export const AgentDeleteConfirmationModal = ({ onClose, agentName, agentId, onDelete }: Props) => {
	const { formatMessage } = useIntl();
	const [deleteError, setDeleteError] = useState<string | undefined>();
	const [isDeletePending, setIsDeletePending] = useState(false);

	const handleDeleteAgent = useCallback(async () => {
		if (agentId) {
			setIsDeletePending(true);
			try {
				await onDelete?.();
				onClose?.();
			} catch (error: any) {
				setDeleteError(
					formatMessage(
						messages[agentResponseMessageTemplate[error?.message_template] as Messages],
					),
				);
			} finally {
				setIsDeletePending(false);
			}
		}
	}, [agentId, formatMessage, onClose, onDelete]);

	if (!agentId) {
		return null;
	}

	return (
		<Modal width="small">
			<ModalHeader>
				<ModalTitle>{formatMessage(messages.title, { agentName })}</ModalTitle>
			</ModalHeader>
			<ModalBody>
				<Text as="p">{formatMessage(messages.body)}</Text>
				{deleteError && (
					<Box xcss={errStyles}>
						<ErrorMessage>{deleteError}</ErrorMessage>
					</Box>
				)}
				<ModalFooter>
					<Button appearance="subtle" onClick={onClose}>
						{formatMessage(messages.cancelText)}
					</Button>
					<Button
						appearance="danger"
						isDisabled={!!deleteError || isDeletePending}
						onClick={(_e, _event: UIAnalyticsEvent) => {
							handleDeleteAgent();
						}}
					>
						{formatMessage(messages.confirmText)}
					</Button>
				</ModalFooter>
			</ModalBody>
		</Modal>
	);
};

export default AgentDeleteConfirmationModal;

const messages = defineMessages({
	cancelText: {
		id: 'ptc-directory.agent-profile.agent-delete-confirm.cancel-text',
		defaultMessage: 'Cancel',
	},
	confirmText: {
		id: 'ptc-directory.agent-profile.agent-delete-confirm.confirm-text',
		defaultMessage: 'Delete',
	},
	title: {
		id: 'ptc-directory.agent-profile.delete-agent-confirm-title',
		defaultMessage: 'Delete "{agentName}"?',
		description: 'Title text for the delete agent confirmation modal',
	},
	body: {
		id: 'ptc-directory.agent-profile.delete-agent-confirm-body',
		defaultMessage: `Are you sure you want to delete this agent? This action cannot be undone.`,
		description: 'Body text for the delete agent confirmation modal',
	},
	error: {
		id: 'ptc-directory.agent-profile.delete-agent-error',
		defaultMessage: 'Could not delete agent',
		description: 'Error message displayed when an agent cannot be deleted',
	},
	errorAgentNotFound: {
		id: 'ptc-directory.agent-profile.delete-agent-error.agent-not-found',
		defaultMessage: 'That agent could not be found.',
		description: 'Error message displayed when an agent cannot be found',
	},
	errorAgentInUse: {
		id: 'ptc-directory.agent-profile.delete-agent-error.agent-in-use',
		defaultMessage: 'Agent is currently in use. Try again later.',
		description: 'Error message displayed when an agent is in use',
	},
	errorIncorrectOwner: {
		id: 'ptc-directory.agent-profile.delete-agent-error.incorrect-owner',
		defaultMessage:
			'You cannot delete agents you have not created yourself. Please contact the agent owner.',
		description: 'Error message displayed when the agent owner is incorrect',
	},
	agentDeletedSuccess: {
		id: 'ptc-directory.agent-profile.delete-agent-success',
		defaultMessage: 'Agent has been deleted!',
		description: 'Success message displayed when an agent is deleted',
	},
});
